import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useClient, useNewNpoAnswer, useUpdateNpoAnswer } from 'shared/apollo';
import {
  RequestState,
  requestError,
  requestInit,
  requestStart,
  requestSuccess,
  setErrorResponse,
} from 'shared/auth/hooks/useRequestState';
import { ProgramContext } from 'shared/providers/ProgramProvider';
import {
  AnswerInput,
  ProgramAnswer,
  ProgramAnswersById,
  ProgramQuestionsResult,
  ProgramQuestionsSection,
  ProgramQuestionWithAnswer,
  ProgramSummary,
  SectionStatus,
  QuestionStatus,
} from 'shared/providers/ProgramProvider/types';
import { useServiceCollections } from 'shared/apollo/pageHooks/useAllServiceCollections';
import {
  serviceQuestionId,
  subServiceQuestionId,
} from 'shared/providers/IndicatorProvider/utils';
import {
  applyImpactSummaryFlow,
  applyProgramTagsToQuestions,
  BENEFICIARY_TERM_QUESTION_ID,
  findQuestion,
  resolveServiceGoal,
  firstUnansweredIn,
  getQuestionStatus,
  getSectionStatus,
  pickInitialQuestionId,
  sectionKey,
  validateAnswerInput,
  QUESTION_CLEAR_MAP,
  isAnswerFilled,
} from 'shared/providers/ProgramProvider/utils';
import { getHelpText } from 'npo/pages/ReportBuilder/Components/ImpactSummary/HelperComponents';
import { logicModalImpactSummary } from 'npo/pages/ReportBuilder/Components/ImpactSummary/utils';
import { updateBulkAnswer } from 'npo/pages/NPOFeedback/MakeEdits/api';
import {
  RawSnapshotQuestion,
  fetchSnapshotQuestions,
} from 'npo/pages/ReportsBeta/ImpactSnapshot/api';
import {
  fetchProgramQuestions,
  saveProgramAnswer as saveProgramAnswerApi,
  updateProgramAnswer as updateProgramAnswerApi,
} from 'shared/providers/ProgramProvider/api';
import { submitReport } from 'npo/pages/ReportBuilder/Components/Review/api';
import { pullReportData, useTrackEvent } from 'shared/utils/useTrackEvent';
import { useReport } from 'shared/providers/ReportProvider';
import { useUserActions } from 'shared/auth/hooks/useUserActions';
import { FeedbackStatus } from 'donor/portal/pages/FeedbackDashboard/api';
import { Answer } from 'donor/portal/pages/FeedbackDashboard/Feedback/api';
import { useAllIndicatorsMin } from 'shared/apollo/pageHooks/useAllIndicators';
import {
  getImpactIndicators,
  REACH_STAGE_ID,
} from 'npo/pages/NPOFeedback/MakeEdits/utils';
import TrueImpactLoading from 'shared/components/TrueImpactLoading';
import TrueImpactConfirmationDialog from 'shared/design-system/components/TrueImpactConfirmationDialog';
import TrueImpactText from 'shared/design-system/base/TrueImpactText';
import { SPACERS } from 'shared/design-system/settings/variables/Spacers';
import styled from 'styled-components';
import { faExclamationTriangle } from '@fortawesome/pro-light-svg-icons';

/**
 * Reshape the flat, section-tagged snapshot question list returned by
 * `snapshotReportQuestions` into the `ProgramQuestionsResult` shape that
 * ProgramProvider consumers (ProgramSidebar / QuestionRenderer /
 * ReviewScreen / ...) expect. Section ordering follows first-appearance
 * in the response. Per-section counters and `nextUnansweredQuestionId`
 * are derived from `isAnswered` since the backend does not compute them
 * for reports.
 */
const reshape = (
  reportId: number,
  raw: RawSnapshotQuestion[],
): ProgramQuestionsResult => {
  const bySection = new Map<string, ProgramQuestionWithAnswer[]>();
  const sectionOrder: ProgramQuestionsSection['section'][] = [];
  raw.forEach(q => {
    const key = q.section ?? '';
    if (!bySection.has(key)) {
      bySection.set(key, []);
      sectionOrder.push(q.section);
    }
    const answer = q.answers[0] ?? null;
    const question: ProgramQuestionWithAnswer = {
      questionId: q.dbId,
      text: q.text,
      name: q.name,
      section: q.section,
      sectionStart: q.sectionStart,
      questionType: q.questionType,
      required: q.required,
      helpContent: q.helpContent,
      reportingType: q.reportingType,
      nextQuestionId: q.nextQuestionId,
      answer,
      // Backend doesn't expose is_answered on NpoReportQuestion — derive
      // from the joined answer row instead. An answer row may exist with
      // empty text/date after a bulk-clear cascade, so gate on
      // isAnswerFilled so cleared questions flip back to incomplete in
      // sidebar and section counters.
      isAnswered: isAnswerFilled(answer),
      isProgramQuestion: q.isProgramQuestion ?? false,
      enumResponses: q.enumResponses || [],
      validations: q.validations || [],
    };
    bySection.get(key)!.push(question);
  });

  const sections: ProgramQuestionsSection[] = sectionOrder.map(section => {
    const questions = bySection.get(section ?? '')!;
    const total = questions.length;
    const answered = questions.filter(q => q.isAnswered).length;
    const firstUnanswered =
      questions.find(q => !q.isAnswered)?.questionId ?? null;
    return {
      section,
      total,
      answered,
      firstUnansweredQuestionId: firstUnanswered,
      questions,
    };
  });

  const totalQuestions = sections.reduce((n, s) => n + s.total, 0);
  const answeredCount = sections.reduce((n, s) => n + s.answered, 0);
  const nextUnansweredQuestionId =
    sections.map(s => s.firstUnansweredQuestionId).find(id => id != null) ??
    null;

  return {
    programId: reportId, // context field is misnamed for us; sidebar only reads .name
    totalQuestions,
    answeredCount,
    completed: totalQuestions > 0 && answeredCount === totalQuestions,
    nextUnansweredQuestionId,
    sections,
  };
};

/**
 * Build a local ProgramAnswer row from the AnswerInput we just persisted
 * plus the dbId echoed back by `newNpoAnswer` / `updateNpoAnswer`. Only
 * the fields the wizard actually reads (dbId / text / date1 / date2 /
 * answerId / questionText) carry meaningful values; server-managed
 * metadata is filled with best-effort defaults so a follow-up save can
 * still find the row by dbId. A full refetch on next mount will replace
 * these placeholders with the canonical row.
 */
const buildLocalAnswer = (
  reportId: number,
  questionId: number,
  input: AnswerInput,
  dbId: number,
  prev: ProgramAnswer | null,
): ProgramAnswer => ({
  id: prev?.id ?? String(dbId),
  dbId,
  questionId,
  programId: prev?.programId ?? reportId,
  text: input.text ?? null,
  date1: input.date1 ?? null,
  date2: input.date2 ?? null,
  answerId: input.answerId ?? null,
  answerTime: prev?.answerTime ?? null,
  answerUserId: prev?.answerUserId ?? null,
  questionText: input.questionText ?? prev?.questionText ?? null,
  autoAnswered: prev?.autoAnswered ?? false,
  resourceId: prev?.resourceId ?? null,
  enabled: prev?.enabled ?? true,
  createdAt: prev?.createdAt ?? null,
  updatedAt: prev?.updatedAt ?? null,
  enumResponse: prev?.enumResponse ?? null,
});

/**
 * Return a copy of `prev` with the given question's answer replaced.
 * Recomputes the affected section's `answered` / `firstUnansweredQuestionId`
 * counters and the top-level `answeredCount` / `nextUnansweredQuestionId` /
 * `completed` so sidebar status flips consistently.
 */
const patchQuestionAnswer = (
  prev: ProgramQuestionsResult,
  questionId: number,
  answer: ProgramAnswer | null,
): ProgramQuestionsResult => {
  let touched = false;
  const nextSections = prev.sections.map(section => {
    if (!section.questions.some(q => q.questionId === questionId))
      return section;
    touched = true;
    const nextQuestions = section.questions.map(q =>
      q.questionId === questionId
        ? { ...q, answer, isAnswered: isAnswerFilled(answer) }
        : q,
    );
    const answered = nextQuestions.filter(q => q.isAnswered).length;
    const firstUnansweredQuestionId =
      nextQuestions.find(q => !q.isAnswered)?.questionId ?? null;
    return {
      ...section,
      questions: nextQuestions,
      answered,
      firstUnansweredQuestionId,
    };
  });
  if (!touched) return prev;
  const answeredCount = nextSections.reduce((n, s) => n + s.answered, 0);
  const nextUnansweredQuestionId =
    nextSections.map(s => s.firstUnansweredQuestionId).find(id => id != null) ??
    null;
  return {
    ...prev,
    sections: nextSections,
    answeredCount,
    completed: prev.totalQuestions > 0 && answeredCount === prev.totalQuestions,
    nextUnansweredQuestionId,
  };
};

const DependentList = styled.ul`
  margin: ${SPACERS.small} 0 ${SPACERS.medium};
  padding-left: ${SPACERS.medium};
  li {
    margin-bottom: ${SPACERS.tiny};
  }
`;

const asError = (message: string, status = 500) =>
  setErrorResponse({ status, message, isAxiosError: false });

const fail = (error: string) => ({ success: false as const, error });

interface Props {
  reportId: number;
  /**
   * The owning program's numeric ID. Optional so legacy call sites that
   * only have a reportId keep compiling; when provided the snapshot
   * merges the program's answers into questions flagged
   * `isProgramQuestion` so the read-only affordance can show the
   * program-profile answer even though the report itself has none.
   */
  programId?: number | null;
  programName: string;
  reportStage?: string | null;
  reportStatus?: FeedbackStatus | null;
  initialQuestionId?: number | null;
  children: React.ReactNode;
}

/**
 * Impact-snapshot flavour of ProgramProvider. Mounts the shared
 * ProgramContext so ProgramSidebar / ProgramQuestionPane / QuestionRenderer
 * / ReviewScreen work unchanged, but the data source is the dedicated
 * `snapshotReportQuestions` endpoint (server-side filters by the report's
 * reporting_type and excludes program-level / INTRO rows) and mutations
 * go through `useNewNpoAnswer` / `useUpdateNpoAnswer` + `submitReport`.
 * TODO: program-level questions are excluded server-side; wire a
 * "manage in Program Questions" affordance later.
 */
const ReportSnapshotProvider: React.FC<Props> = ({
  reportId,
  programId,
  programName,
  reportStage,
  reportStatus,
  initialQuestionId,
  children,
}) => {
  const client = useClient();
  const trackEvent = useTrackEvent();
  const { userStaticType } = useUserActions();
  const { report: reportState, isReportEndDatePassed } = useReport();
  const [questions, setQuestions] = useState<
    RequestState<ProgramQuestionsResult>
  >(requestInit());
  const [activeQuestionId, setActiveQuestionId] = useState<number | null>(null);
  const [attemptedSubmit, setAttemptedSubmit] = useState(false);
  const [saving, setSaving] = useState<RequestState<{ questionId: number }>>(
    requestInit(),
  );
  const [submitting, setSubmitting] = useState<
    RequestState<{ isCompleted: boolean }>
  >(requestInit());
  const [renaming] = useState<RequestState<{ name: string }>>(requestInit());

  /**
   * Pending clear-cascade confirmation. When set, a modal is rendered
   * that lists the dependent answers about to be wiped and asks the
   * user to confirm. Answering the modal resolves the stored promise,
   * unblocking the in-flight `saveAnswer` call.
   */
  const [pendingClearConfirm, setPendingClearConfirm] = useState<{
    dependents: { questionId: number; title: string; preview: string }[];
    resolve: (ok: boolean) => void;
  } | null>(null);

  const { run: runNew } = useNewNpoAnswer();
  const { run: runUpdate } = useUpdateNpoAnswer();

  // Program answers for questions flagged `isProgramQuestion`. Snapshot's
  // own `answers(filters: { reportId })` join returns only the report's
  // rows, so program-level questions come back without an answer. We fetch
  // the program's answer set once and overlay it into questionsData.
  const [programAnswersByQ, setProgramAnswersByQ] = useState<
    Map<number, ProgramAnswer>
  >(new Map());

  const reloadProgramAnswers = useCallback(async () => {
    if (!client || programId == null) return;
    try {
      const res = await fetchProgramQuestions(client, programId);
      const map = new Map<number, ProgramAnswer>();
      res.data.sections.forEach(s =>
        s.questions.forEach(q => {
          if (q.answer) map.set(q.questionId, q.answer);
        }),
      );
      setProgramAnswersByQ(map);
    } catch {
      // Non-fatal: pane still renders, just without program-answer overlay.
    }
  }, [client, programId]);

  const reloadQuestions = useCallback(async () => {
    if (!client) return;
    setQuestions(requestStart());
    try {
      const raw = await fetchSnapshotQuestions(client, reportId);
      setQuestions(requestSuccess(reshape(reportId, raw)));
    } catch (err) {
      const message =
        err instanceof Error && err.message
          ? err.message
          : 'Failed to load snapshot questions.';
      setQuestions(requestError(asError(message)));
    }
    // Refresh program answers alongside so edits made in the program
    // modal show up on return to the snapshot pane.
    reloadProgramAnswers();
  }, [client, reportId, reloadProgramAnswers]);

  useEffect(() => {
    reloadQuestions();
  }, [reloadQuestions]);

  const questionsRaw =
    questions.type === 'REQUEST_SUCCESS' ? questions.data : null;

  // Service collections feed the `<Service_goal>` placeholder in question
  // text (e.g. Q40021). cache-first, so this is shared with the fields.
  const serviceCollections = useServiceCollections({});

  const questionsData = useMemo(() => {
    // Q40002 / Q40003 are usually program-level and therefore absent
    // from the snapshot rows; fall back to the program-profile answer.
    const answerText = (id: number) =>
      findQuestion(questionsRaw, id)?.question.answer?.text ??
      programAnswersByQ.get(id)?.text ??
      null;
    const serviceGoal = resolveServiceGoal(
      serviceCollections.type === 'REQUEST_SUCCESS'
        ? serviceCollections.data
        : null,
      answerText(serviceQuestionId),
      answerText(subServiceQuestionId),
    );
    return applyImpactSummaryFlow(
      applyProgramTagsToQuestions(questionsRaw, programName, {
        beneficiaryTerm: answerText(BENEFICIARY_TERM_QUESTION_ID),
        serviceGoal,
      }),
    );
  }, [questionsRaw, programName, programAnswersByQ, serviceCollections]);

  // Exposed via context so the pane can pre-fill (disabled) inputs with
  // the program-profile answer for `isProgramQuestion` rows without
  // polluting the report's own answer graph — otherwise `isAnswerFilled`
  // in the shared sidebar / status helpers would flip the sidebar to
  // "complete" before the user hits Save & Continue.
  const getProgramProfileAnswer = useCallback(
    (questionId: number) => programAnswersByQ.get(questionId) ?? null,
    [programAnswersByQ],
  );

  const questionsForContext = useMemo<RequestState<ProgramQuestionsResult>>(
    () =>
      questions.type === 'REQUEST_SUCCESS' && questionsData
        ? requestSuccess<ProgramQuestionsResult>(questionsData)
        : questions,
    [questions, questionsData],
  );

  useEffect(() => {
    if (!questionsData) return;
    setActiveQuestionId(prev => {
      if (prev != null) return prev;
      if (initialQuestionId != null) {
        const exists = questionsData.sections.some(s =>
          s.questions.some(q => q.questionId === initialQuestionId),
        );
        if (exists) return initialQuestionId;
      }
      return pickInitialQuestionId(questionsData);
    });
  }, [questionsData, initialQuestionId]);

  const program = useMemo<RequestState<ProgramSummary>>(
    () =>
      requestSuccess<ProgramSummary>({
        id: reportId,
        dbId: reportId,
        name: programName,
        isCompleted: Boolean(questionsData?.completed),
      }),
    [reportId, programName, questionsData?.completed],
  );

  const isAllComplete = Boolean(questionsData?.completed);

  const sectionStatus = useCallback(
    (s: ProgramQuestionsSection): SectionStatus =>
      getSectionStatus(s, attemptedSubmit),
    [attemptedSubmit],
  );

  const questionStatus = useCallback(
    (q: ProgramQuestionWithAnswer): QuestionStatus =>
      getQuestionStatus(q, attemptedSubmit),
    [attemptedSubmit],
  );

  const findQuestionById = useCallback(
    (id: number | null | undefined) => findQuestion(questionsData, id),
    [questionsData],
  );

  const { answers, answersTextById, answersArray } = useMemo<{
    answers: ProgramAnswersById;
    answersTextById: { [id: number]: string | undefined };
    answersArray: Answer[];
  }>(() => {
    const map: ProgramAnswersById = {};
    const textById: { [id: number]: string | undefined } = {};
    const collected: Answer[] = [];
    if (!questionsData)
      return {
        answers: map,
        answersTextById: textById,
        answersArray: collected,
      };
    const snapshotQuestionIds = new Set<number>();
    questionsData.sections.forEach(s => {
      s.questions.forEach(q => {
        snapshotQuestionIds.add(q.questionId);
        if (q.answer && q.answer.text != null) {
          map[q.questionId] = {
            questionId: q.questionId,
            sectionKey: q.section,
            text: q.answer.text,
          };
          textById[q.questionId] = q.answer.text;
          collected.push({
            questionId: q.questionId,
            text: q.answer.text,
          } as Answer);
        }
      });
    });
    // Program-level questions are excluded from the snapshot server-side,
    // but cross-question helpers (Q40004 suggested term / Q40006 helper
    // text / succeed indicators) key off e.g. Q40003. Fall back to the
    // program-profile answer for ids the snapshot does not carry at all.
    programAnswersByQ.forEach((a, questionId) => {
      if (snapshotQuestionIds.has(questionId) || a.text == null) return;
      map[questionId] = { questionId, sectionKey: null, text: a.text };
      textById[questionId] = a.text;
      collected.push({ questionId, text: a.text } as Answer);
    });
    return { answers: map, answersTextById: textById, answersArray: collected };
  }, [questionsData, programAnswersByQ]);

  const getHelperText = useCallback(
    (
      sectionKeyValue: string | null | undefined,
      questionId: number,
    ): React.ReactNode => {
      // Only impact-summary questions carry cross-answer helper text today.
      if (sectionKey(sectionKeyValue) !== 'impact_summary') return null;
      const cause = questionId === 40004 ? answers[40003]?.text : undefined;
      const mainBeneficiaryText = cause
        ? (logicModalImpactSummary as Record<string, string>)[cause]
        : undefined;
      return getHelpText(
        questionId,
        answersArray,
        null,
        mainBeneficiaryText,
        undefined,
      );
    },
    [answers, answersArray],
  );

  const {
    data: indicators,
    loading: loadingIndicators,
  } = useAllIndicatorsMin();

  const allSucceedIndicators = useMemo(() => {
    const serviceAnswer = answers[40003]?.text;
    return indicators && serviceAnswer
      ? getImpactIndicators(indicators, serviceAnswer)
      : undefined;
  }, [indicators, answers]);

  const allReachIndicators = useMemo(() => {
    const serviceAnswer = answers[40003]?.text;
    return indicators && serviceAnswer
      ? getImpactIndicators(indicators, serviceAnswer, REACH_STAGE_ID)
      : undefined;
  }, [indicators, answers]);

  /**
   * Persist an answer for a snapshot question. Uses newNpoAnswer for a
   * first save and updateNpoAnswer for edits; both hand back the answer's
   * dbId plus a server-picked `nextQuestion` (we derive ordering from the
   * flat question index instead, so nextQuestion is ignored). On success
   * we patch the questions cache in-place from the persisted input +
   * echoed dbId — avoiding the full `snapshotReportQuestions` refetch
   * that used to blank the wizard while inflight and force legacy
   * `initialValue`-only fields (BudgetSummary, BudgetUpload) to remount
   * with stale/empty values.
   */
  const saveAnswer = useCallback(
    async (
      question: ProgramQuestionWithAnswer,
      input: AnswerInput,
    ): Promise<
      | { success: true; nextUnansweredQuestionId: number | null }
      | { success: false; error: string }
    > => {
      if (!client) return fail('Not ready.');
      const validationError = validateAnswerInput(question, input);
      if (validationError) return fail(validationError);

      // Detect dependent questions to clear when this answer's text
      // actually changes. Direct children only, per QUESTION_CLEAR_MAP.
      // Text-only comparison; first-time answers (empty -> value) do not
      // trigger the cascade.
      const dependents = QUESTION_CLEAR_MAP[question.questionId] ?? [];
      const previousText = question.answer?.text ?? '';
      const nextText = input.text ?? '';
      const textChanged =
        dependents.length > 0 &&
        previousText !== nextText &&
        previousText !== '';
      const dependentsToClear: {
        questionId: number;
        title: string;
        preview: string;
      }[] = [];
      if (textChanged && questions.type === 'REQUEST_SUCCESS') {
        for (const depId of dependents) {
          const found = findQuestion(questions.data, depId);
          if (!found) continue;
          const depQ = found.question;
          const answerText = depQ.answer?.text ?? '';
          if (answerText.trim().length === 0) continue;
          dependentsToClear.push({
            questionId: depId,
            title: depQ.text ?? depQ.name ?? `Question ${depId}`,
            preview:
              answerText.length > 80
                ? `${answerText.slice(0, 80)}\u2026`
                : answerText,
          });
        }
      }
      if (dependentsToClear.length > 0) {
        const ok = await new Promise<boolean>(resolve => {
          setPendingClearConfirm({ dependents: dependentsToClear, resolve });
        });
        if (!ok) return fail('Save canceled.');
      }

      setSaving(requestStart());
      try {
        const existingId = question.answer?.dbId ?? null;
        // useNewNpoAnswer / useUpdateNpoAnswer both invoke onFinish with
        // (id, nextQuestion): `id` keeps the cache patch coherent for the
        // next save; `nextQuestion` is the backend-resolved branch target
        // (e.g. Q40006 "indirectly" -> Q40022, "directly" -> Q40010) and
        // is surfaced to the wizard as `nextUnansweredQuestionId`.
        const { savedAnswerId, serverNextId } = await new Promise<{
          savedAnswerId: number;
          serverNextId: number | null;
        }>((resolve, reject) => {
          const done = (id: number, nextQuestion?: { dbId?: number }) =>
            resolve({ savedAnswerId: id, serverNextId: nextQuestion?.dbId ?? null });
          try {
            if (existingId) {
              runUpdate(
                {
                  id: existingId,
                  reportId,
                  questionId: question.questionId,
                  ...input,
                  ignoreClearData: true,
                },
                done,
              );
            } else {
              runNew(
                { reportId, questionId: question.questionId, ...input },
                done,
              );
            }
          } catch (err) {
            reject(err);
          }
        });
        if (dependentsToClear.length > 0) {
          try {
            await updateBulkAnswer(
              client,
              reportId,
              dependentsToClear.map(d => ({
                id: undefined,
                answerId: undefined,
                ignoreClearData: true,
                questionId: d.questionId,
                text: '',
              })),
            );
          } catch (clearErr) {
            // eslint-disable-next-line no-console
            console.error('Failed to bulk-clear dependent answers', clearErr);
          }
        }
        // Patch the just-saved question and any cascade-cleared dependents
        // in-place. No refetch means legacy `initialValue`-only fields
        // don't get remounted against a transient empty cache.
        setQuestions(prev => {
          if (prev.type !== 'REQUEST_SUCCESS') return prev;
          const savedAnswer = buildLocalAnswer(
            reportId,
            question.questionId,
            input,
            savedAnswerId ?? existingId ?? 0,
            question.answer ?? null,
          );
          let next = patchQuestionAnswer(
            prev.data,
            question.questionId,
            savedAnswer,
          );
          for (const d of dependentsToClear) {
            next = patchQuestionAnswer(next, d.questionId, null);
          }
          return requestSuccess(next);
        });
        setSaving(requestSuccess({ questionId: question.questionId }));
        // Only hand back a backend next-question id the snapshot actually
        // carries (raw, pre-flow list — a newly revealed Q40022 counts);
        // anything else (another section, excluded rows) falls back to
        // the wizard's own flat ordering.
        const serverNextKnown =
          serverNextId != null &&
          questions.type === 'REQUEST_SUCCESS' &&
          findQuestion(questions.data, serverNextId) != null;
        return {
          success: true,
          nextUnansweredQuestionId: serverNextKnown ? serverNextId : null,
        };
      } catch (err) {
        const message =
          err instanceof Error && err.message
            ? err.message
            : 'Failed to save answer.';
        setSaving(requestError(asError(message)));
        return fail(message);
      }
    },
    [client, reportId, runNew, runUpdate, questions],
  );

  const submitSnapshot = useCallback(async () => {
    if (!isAllComplete) {
      setAttemptedSubmit(true);
      return fail(
        'Some sections are still incomplete. Fill the highlighted questions before submitting.',
      );
    }
    if (!client) return fail('Not ready.');
    setSubmitting(requestStart());
    try {
      const targetStatus: FeedbackStatus = 'Published';
      await submitReport(
        client,
        reportId,
        targetStatus,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (reportStage ?? undefined) as any,
      );
      const reportData =
        reportState.type === 'REQUEST_SUCCESS' ? reportState.data : undefined;
      trackEvent('ReportStatusChanged', {
        ...(reportData
          ? pullReportData(reportData, isReportEndDatePassed, userStaticType)
          : {}),
        reportId,
        previousStatus: reportStatus,
        previousStage: reportStage,
        status: targetStatus,
        stage: reportStage,
      });
      setSubmitting(requestSuccess({ isCompleted: true }));
      return { success: true as const, nextUnansweredQuestionId: null };
    } catch (err) {
      const message =
        err instanceof Error && err.message
          ? err.message
          : 'Failed to submit snapshot.';
      setSubmitting(requestError(asError(message)));
      return fail(message);
    }
  }, [client, reportId, reportStage, isAllComplete]);

  const notSupported = useCallback(
    async () => fail('Not supported in snapshot mode.'),
    [],
  );

  const [savingProgramAnswer, setSavingProgramAnswer] = useState<
    RequestState<{ questionId: number }>
  >(requestInit());

  /**
   * Overwrite the owning *program-profile* answer for `questionId` with
   * the given `input`. Distinct from `saveAnswer` above, which persists
   * to the *report*. Used by the "overwrite program answer" confirmation
   * shown when a user saves a report-level answer that diverges from
   * the program-level one. Updates the local program-answer overlay so
   * subsequent renders reflect the new value without a refetch.
   */
  const saveProgramAnswer = useCallback(
    async (
      questionId: number,
      input: AnswerInput,
    ): Promise<
      | { success: true; nextUnansweredQuestionId: number | null }
      | { success: false; error: string }
    > => {
      if (!client || programId == null)
        return fail('Program is not available.');
      setSavingProgramAnswer(requestStart());
      try {
        const existing = programAnswersByQ.get(questionId);
        const existingId = existing?.dbId ?? null;
        const res = existingId
          ? await updateProgramAnswerApi(client, existingId, input)
          : await saveProgramAnswerApi(client, programId, questionId, input);
        const payload = res.data ?? undefined;
        if (!payload?.success) {
          const message = 'Failed to save program answer.';
          setSavingProgramAnswer(requestError(asError(message)));
          return fail(message);
        }
        // Patch local overlay so getProgramProfileAnswer reflects the
        // new value immediately.
        setProgramAnswersByQ(prev => {
          const next = new Map(prev);
          const base: ProgramAnswer =
            existing ??
            ({
              id: String(payload.id ?? questionId),
              dbId: payload.id ?? null,
              questionId,
              programId: programId ?? 0,
              text: null,
              date1: null,
              date2: null,
              answerId: null,
              answerTime: null,
              answerUserId: null,
              questionText: null,
              autoAnswered: null,
              resourceId: null,
              enabled: true,
              createdAt: null,
              updatedAt: null,
              enumResponse: null,
            } as ProgramAnswer);
          const nextAnswer: ProgramAnswer = {
            ...base,
            dbId: payload.id ?? base.dbId ?? null,
            text: input.text ?? base.text,
            date1: input.date1 ?? base.date1,
            date2: input.date2 ?? base.date2,
            resourceId: input.resourceId ?? base.resourceId,
          };
          next.set(questionId, nextAnswer);
          return next;
        });
        setSavingProgramAnswer(requestSuccess({ questionId }));
        return { success: true, nextUnansweredQuestionId: null };
      } catch (err) {
        const message =
          err instanceof Error && err.message
            ? err.message
            : 'Failed to save program answer.';
        setSavingProgramAnswer(requestError(asError(message)));
        return fail(message);
      }
    },
    [client, programId, programAnswersByQ],
  );

  const value = useMemo(
    () => ({
      programId: reportId,
      program,
      programName,
      questions: questionsForContext,
      activeQuestionId,
      setActiveQuestionId,
      attemptedSubmit,
      setAttemptedSubmit,
      isAllComplete,
      sectionStatus,
      questionStatus,
      findQuestionById,
      firstUnansweredInSection: firstUnansweredIn,
      validate: validateAnswerInput,
      getHelperText,
      answers,
      answersTextById,
      answersArray,
      getProgramProfileAnswer,
      allSucceedIndicators,
      allReachIndicators,
      loadingIndicators,
      reloadQuestions,
      reloadProgram: async () => { /* noop */ },
      saveAnswer,
      saveProgramAnswer,
      deleteAnswer: notSupported,
      updateProgramName: notSupported,
      submitProgram: submitSnapshot,
      reopenProgram: notSupported,
      copyProgramAnswersToReport: notSupported,
      saving,
      savingProgramAnswer,
      submitting,
      renaming,
    }),
    [
      reportId,
      program,
      questionsForContext,
      activeQuestionId,
      attemptedSubmit,
      isAllComplete,
      sectionStatus,
      questionStatus,
      findQuestionById,
      getHelperText,
      answers,
      answersTextById,
      answersArray,
      getProgramProfileAnswer,
      programName,
      allSucceedIndicators,
      allReachIndicators,
      loadingIndicators,
      reloadQuestions,
      saveAnswer,
      saveProgramAnswer,
      submitSnapshot,
      notSupported,
      saving,
      savingProgramAnswer,
      submitting,
      renaming,
    ],
  );

  if (loadingIndicators) {
    return <TrueImpactLoading />;
  }

  const resolvePendingConfirm = (ok: boolean) => {
    if (!pendingClearConfirm) return;
    pendingClearConfirm.resolve(ok);
    setPendingClearConfirm(null);
  };

  return (
    <ProgramContext.Provider value={value}>
      {children}
      <TrueImpactConfirmationDialog
        open={pendingClearConfirm != null}
        icon={faExclamationTriangle}
        iconColor="warning"
        handleClose={() => resolvePendingConfirm(false)}
        previous={{
          text: 'Cancel',
          onClick: () => resolvePendingConfirm(false),
        }}
        next={{
          text: 'Continue',
          onClick: () => resolvePendingConfirm(true),
        }}
      >
        <TrueImpactText variant="heading-subheading" align="center">
          Clear dependent answers?
        </TrueImpactText>
        <TrueImpactText variant="body" align="center">
          Changing this answer will clear the following answers:
        </TrueImpactText>
        <DependentList>
          {(pendingClearConfirm?.dependents ?? []).map(d => (
            <li key={d.questionId}>
              <TrueImpactText variant="body-bold">{d.title}</TrueImpactText>
              <TrueImpactText variant="body-small" color="greyDark">
                Current: {d.preview}
              </TrueImpactText>
            </li>
          ))}
        </DependentList>
      </TrueImpactConfirmationDialog>
    </ProgramContext.Provider>
  );
};

export default ReportSnapshotProvider;
